import Promise from 'bluebird';
import utils from 'web3-utils';
import { keyBy } from 'lodash';
import safeToBn from '@blockcatio/tabby-library/lib/utilities/safeToBn';
import fetchContractEvents from '@blockcatio/tabby-library/lib/utilities/fetchContractEvents';

import { ActionStatusEnum } from '../../constants';

const BigNumber = require('bignumber.js');

/**
 * The TabbyPay contract
 */
class TabbyPayContractv1 {
  constructor(config, eth) {
    this.id = config.id;
    this.config = {
      accountFetchPosition: 0,
      initialBlockNumber: 0,
      ...config,
    };
    this.contract = new eth.web3Eth.Contract(config.abi);
    this.contract.options.address = config.address;
    this.eth = eth;

    this.fetchPaymentEvents = this.fetchPaymentEvents.bind(this);
    this.eventToPayment = this.eventToPayment.bind(this);
  }

  getAddress() {
    return this.config.address;
  }

  /**
   * Creates the transaction for sending a payment
   *
   * @param {Object} paymentDetails
   * @param {String} paymentDetails.destination
   * @param {String} paymentDetails.sender
   * @param {String} paymentDetails.amount (in wei)
   * @param {String} paymentFee
   * @param {String} gasPrice
   * @param {String?} gasLimit
   * @return {Object}
   */
  sendPaymentTransaction(
    paymentDetails,
    paymentFee,
    gasPrice,
    gasLimit = this.config.createPaymentGasLimit,
  ) {
    return {
      transaction: this.contract.methods.createPayment(paymentDetails.destination),
      contract: this.config.address,
      sendOptions: {
        to: paymentDetails.destination,
        from: paymentDetails.sender,
        value: safeToBn(paymentDetails.amount)
          .add(paymentFee)
          .toString(10),
        gasPrice,
        gas: gasLimit,
      },
    };
  }

  /**
   * Creates the transaction for accepting a payment
   *
   * @param {Number} id
   * @return {Object}
   */
  acceptPaymentTransaction(
    { id },
    address,
    gasPrice,
    gasLimit = this.config.acceptPaymentGasLimit,
  ) {
    return {
      transaction: this.contract.methods.acceptPayment(id),
      contract: this.config.address,
      sendOptions: {
        from: address,
        gasPrice,
        gas: gasLimit,
      },
    };
  }

  /**
   * Creates the transaction for cancelling a payment
   *
   * @param {Number} id
   * @return {Object}
   */
  cancelPaymentTransaction(
    { id },
    address,
    gasPrice,
    gasLimit = this.config.cancelPaymentGasLimit,
  ) {
    return {
      transaction: this.contract.methods.cancelPayment(id),
      contract: this.config.address,
      sendOptions: {
        from: address,
        gasPrice,
        gas: gasLimit,
      },
    };
  }

  /**
   * Fetches all payments for a given address, searching events up to latest block number.
   *
   * @param {String} address
   * @param {Number} latestBlockNumber
   * @return {Promise<Object[]>} - the payments
   */
  fetchPayments(address, latestBlockNumber) {
    return this.fetchPaymentEvents(address, latestBlockNumber, this.eventToPayment);
  }

  /**
   * Fetch payment events and transform them into payment objects.
   *
   * @param {String} address
   * @param {Number} latestBlockNumber
   * @param {Function} eventFormatter
   */
  async fetchPaymentEvents(address, latestBlockNumber, eventFormatter) {
    const addrLower = address.toLowerCase();

    // Fetch create events first (build list of ids, blocks)
    const createEvents = await Promise.reduce(
      [{ destination: address }, { sender: address }].map(filter =>
        fetchContractEvents(this.contract, {
          eventName: 'PaymentCreated',
          fromBlock: this.config.initialBlockNumber,
          toBlock: latestBlockNumber,
          filter,
        })),
      (out, events) => (events ? [...out, ...events] : [...out]),
    );

    // Fetch more info/history if we found events.
    if (createEvents.length) {
      // Fetch blocks and events against those ids in parallel.
      const fetchBlocks = this.eth.fetchBlocks(createEvents.map(event => event.blockNumber));
      const fetchRestOfEvents = Promise.reduce(
        ['PaymentAccepted', 'PaymentCancelled'].map(eventName =>
          fetchContractEvents(this.contract, {
            eventName,
            fromBlock: this.config.initialBlockNumber,
            toBlock: latestBlockNumber,
            filter: {
              id: createEvents.map(event => event.returnValues.id),
            },
          })),
        (out, events) => (events ? [...out, ...events] : [...out]),
      );

      // Wait for data.
      const [blocks, restOfEvents] = await Promise.all([fetchBlocks, fetchRestOfEvents]);
      const restOfEventsKeyed = restOfEvents.reduce(
        (out, item) => ({ ...out, [item.returnValues.id]: item }),
        {},
      );

      const blocksByNum = keyBy(blocks, 'blockNumber');

      // Convert to our Payment shape.
      return createEvents
        .map(event => eventFormatter(event, addrLower, blocksByNum))
        .map((event) => {
          const nextEvent = restOfEventsKeyed[event.id];
          if (nextEvent && nextEvent.event === 'PaymentAccepted') {
            return {
              ...event,
              status: ActionStatusEnum.accepted,
              blockNumber: nextEvent.blockNumber,
            };
          }
          if (nextEvent && nextEvent.event === 'PaymentCancelled') {
            return {
              ...event,
              status: ActionStatusEnum.cancelled,
              blockNumber: nextEvent.blockNumber,
            };
          }
          return event;
        })
        .sort((a, b) => parseInt(a.id, 10) - parseInt(b.id, 10))
        .reduce((out, payment) => ({ ...out, [payment.transactionHash]: payment }), {});
    }
    return [];
  }

  async fetchEventsSince(fromBlock) {
    return fetchContractEvents(this.contract, {
      fromBlock,
      toBlock: 'latest',
    })
      .then(events => events.filter(event => event.event.indexOf('Payment') === 0))
      .then(events =>
        events.map(event => ({
          ...event,
          contract: this.id,
        })));
  }

  async fetchPaymentFeePercentage() {
    return this.contract.methods
      .feePercentage()
      .call()
      .then(result => BigNumber(utils.fromWei(result, 'ether')));
  }

  async fetchPaymentDiscountPercentage(address) {
    return this.contract.methods
      .calculateDiscountPercentage(address)
      .call()
      .then(result => BigNumber(utils.fromWei(result, 'ether')));
  }

  /**
   * Generate a payment object from a CreatePayment event.
   *
   * @param {Object} event
   * @param {String|address} address
   * @param {Object} blocksByNum
   *
   * @return {Object}
   */
  eventToPayment(event, address, blocksByNum) {
    return {
      id: event.returnValues.id,
      destination: event.returnValues.destination.toLowerCase(),
      sender: event.returnValues.sender.toLowerCase(),
      amount: event.returnValues.amount,
      sentTimestamp: blocksByNum[event.blockNumber].timestamp,
      blockNumber: event.blockNumber,
      status: ActionStatusEnum.pending,
      transactionHash: event.transactionHash,
      contract: this.id,
      type: event.returnValues.destination.toLowerCase() === address ? 'incoming' : 'outgoing',
    };
  }
}

export default TabbyPayContractv1;



// WEBPACK FOOTER //
// ./src/services/TabbyPayContracts/TabbyPayContractv1.js